/**
 * The player's signalling socket, parsed at the boundary. See docs/rules/api_boundary.md.
 */

import type { ViolationOrigin, ViolationSink } from '@/types/contract';
import { ViolationSourceKind } from '@/types/contract';
import {
    CodecUnsupportedFrameSchema, IceFrameSchema, OfferFrameSchema, type SignallingFrame,
} from '@/types/webrtc';

import { getKeyedOption, parseKeyedFrame } from './contract';
import { contractReporter } from './contractReporter';

const SIGNALLING_OPTIONS = [
    getKeyedOption('sdp', OfferFrameSchema),
    getKeyedOption('ice', IceFrameSchema),
    getKeyedOption('error', CodecUnsupportedFrameSchema),
] as const;

/** The socket's folder: its path without the camera list, which each session changes. */
export function getSignallingEndpoint(url: string): string {
    return new URL('.', url).pathname;
}

/** Reads the signalling frames of the socket at `url`, which the server `serverId` sends. */
export function createSignallingDecoder(
    url: string, serverId: string, sink: ViolationSink = contractReporter,
): (text: string) => SignallingFrame | null {
    const origin: ViolationOrigin = {
        endpoint: getSignallingEndpoint(url),
        source: {
            kind: ViolationSourceKind.Relay,
            serverId,
        },
    };
    return (text: string): SignallingFrame | null =>
        parseKeyedFrame(SIGNALLING_OPTIONS, text, origin, sink);
}
